import React, { useEffect, useState, useCallback } from 'react';
import cx from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { getCategoryName } from '../../../../utils/utils';
import { IconExplore } from '../../../../../static/icons/iconExplore';
import { Typography } from '../../../atoms/typography/Typography';
import { Stack } from '../../../atoms/stack/Stack';
import ConfirmationModal from '../../../molecules/shared/confirmationModal/ConfirmationModal';
import SelectCategoryContent from '../selectCategoryContent/SelectCategoryContent';
import SystemGuideSteps from '../systemGuideSteps/SystemGuideSteps';
import ProgressLine from '../../../molecules/shared/progressLine/ProgressLine';
import CategorySearch from '../../../molecules/systemGuide/categorySearch/CategorySearch';
import { Loader } from '../../../atoms/loader/Loader';

/*
const groupByOrder = (array) => {
  const grouped = {};
  array.forEach((item) => {
    const groupKey = Math.floor(item.order);

    if (!grouped[groupKey]) {
      grouped[groupKey] = [item];
    } else {
      grouped[groupKey].push(item);
    }
  });
  return Object.values(grouped);
};
*/

const SystemGuideModal = ({
  isOpen,
  guideQuestions,
  categories,
  selectedCategory,
  onSelectCategory,
  locale,
  onAbort,
  onComplete,
  onSaveProgress,
  startGuideLoading,
  completeGuideLoading,
  t,
}) => {
  const totalSteps = guideQuestions.length - 1;
  // const totalSteps = 8;
  const selectedCategoryName = getCategoryName(selectedCategory, categories);
  const [steps, setSteps] = useState([0]);
  const scrollRef = React.useRef(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [, setOpenSearch] = useState(false);
  const [expandedSearchResult, setExpandedSearchResult] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const methods = useForm({
    defaultValues: {},
  });

  const currentStep = steps[steps.length - 1];
  const questions = guideQuestions;
  // const questions = groupByOrder(guideQuestions, 'order');

  const currentQuestion = questions[currentStep];
  const disabledNext =
    currentQuestion?.required &&
    (!methods.watch(`question-${currentQuestion.id}`) ||
      methods.watch(`question-${currentQuestion.id}`).length === 0);

  /*
  const currentQuestionArray = questions[currentStep - 1];
  const disabledNext = currentQuestionArray?.find(
    (el) =>
      el.required &&
      (!methods.watch(`question-${el.id}`) ||
        methods.watch(`question-${el.id}`).length === 0)
  );
  */

  useEffect(() => {
    setFilteredCategories(categories);
  }, [categories]);

  const searchCategory = useCallback(() => {
    const searchQuery = query
      .replace(/[\W_]+/gi, '')
      .toLocaleLowerCase()
      .trim();

    const filteredCategoriesArr =
      searchQuery === '' || searchQuery?.length < 2
        ? categories
        : Object.entries(categories)
            .filter(([topCategoryName, categoriesData]) => {
              if (
                topCategoryName
                  .toLocaleLowerCase()
                  .replace(/[\W_]+/gi, '')
                  .includes(searchQuery)
              ) {
                return true;
              }
              if (
                categoriesData.filter((catObject) =>
                  catObject.name
                    .toLocaleLowerCase()
                    .replace(/[\W_]+/gi, '')
                    .includes(searchQuery)
                ).length > 0
              ) {
                return true;
              }
              if (
                categoriesData.filter(
                  (catObject) =>
                    catObject?.alternativeNames?.filter((match) =>
                      match?.alternativeName
                        ?.toLocaleLowerCase()
                        .replace(/[\W_]+/gi, '')
                        .includes(searchQuery)
                    ).length > 0
                ).length > 0
              ) {
                return true;
              }
              return false;
            })
            .map(([topCategoryName, categoriesData]) => {
              const sortedCategoriesData = [...categoriesData]
                .sort((a, b) => {
                  console.log('a', a.name);
                  console.log('b', b.name);
                  console.log(a.name.localeCompare(b.name));

                  return a.name.localeCompare(b.name);
                })
                .sort((a, b) => {
                  if (
                    a.name
                      .toLocaleLowerCase()
                      .replace(/[\W_]+/gi, '')
                      .includes(searchQuery)
                  ) {
                    return -1;
                  }
                  if (
                    b.name
                      .toLocaleLowerCase()
                      .replace(/[\W_]+/gi, '')
                      .includes(searchQuery)
                  ) {
                    return 1;
                  }
                  return 0;
                });
              return [topCategoryName, sortedCategoriesData];
            })
            .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {});

    setExpandedSearchResult(query?.length >= 2);
    setFilteredCategories(filteredCategoriesArr);
  }, [query, categories]);

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      searchCategory();
      setOpenSearch(true);
    }
  }, [debouncedQuery, searchCategory]);

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', isOpen);
  }, [isOpen]);

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  const scrollToTop = () => {
    if (scrollRef.current) {
      const timeout = setTimeout(() => {
        scrollRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        clearTimeout(timeout);
      }, 100);
    }
  };

  const handleNextStep = () => {
    if (currentStep === totalSteps || disabledNext) return;
    const selectedAnswers = methods.watch(`question-${currentQuestion.id}`);
    const selectedAnswerObject =
      selectedAnswers?.[0] &&
      currentQuestion.guideAnswers?.find(
        (answer) => answer.id === selectedAnswers[0].answerId
      );

    /*
    const selectedAnswerId = methods.watch(
      `question-${currentQuestionArray[0].id}`
    );

    const selectedAnswerObject = currentQuestionArray[0].guideAnswers?.find(
      (answer) => answer.id === selectedAnswerId
    );
    */

    let nextStep = currentStep + 1;

    if (selectedAnswerObject?.nextQuestion) {
      nextStep = questions.findIndex(
        (q) => q.order === Number(selectedAnswerObject.nextQuestion)
      );
    }

    if (currentQuestion.name === 'WHICH_SYSTEM') {
      nextStep = questions.findIndex((q) => q.name === 'WHAT_IS_SYSTEM') + 1;
    }

    const nextQuestionId = questions[nextStep]?.id ?? null;

    // Save progress
    onSaveProgress?.(currentQuestion.id, selectedAnswers, nextQuestionId);

    setSteps([...steps, nextStep]);
    scrollToTop();
  };

  const handlePreviousStep = () => {
    // Remove all previously selected answers
    // methods.setValue(`question-${currentQuestion.id}`, undefined);

    /*
    currentQuestionArray.forEach((visibleQuestion) => {
      methods.setValue(`question-${visibleQuestion.id}`, undefined);
    });
    */

    if (steps.length > 1) {
      setSteps((prevSteps) => prevSteps.slice(0, -1));
      scrollToTop();
    }
  };

  const handleClose = () => {
    setShowConfirmation((prev) => !prev);
  };

  const confirmClose = (data) => {
    setShowConfirmation(false);
    onAbort(data);
  };

  if (isOpen && startGuideLoading) {
    return (
      <div className="fixed top-0 left-0 w-full h-full sm:px-5 sm:py-10 z-[9999] flex items-center justify-center bg-systemguideBg">
        <div className="relative flex items-center justify-center bg-white w-full h-full max-w-4xl sm:rounded-[40px] transition-opacity duration-500 opacity-100">
          <Loader />

          <div className="absolute flex justify-center w-full h-12 bg-transparent top-0 sm:-top-6">
            <div className="flex flex-row w-full sm:w-40 h-full px-3 gap-1 bg-white shadow-md sm:rounded-[40px] justify-center items-center">
              <IconExplore height={24} width={24} />
              <Typography
                fontVariant="sm"
                className="w-fit leading-none !text-primary-800 font-normal"
              >
                {t('common.systemGuide')}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isOpen && (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <FormProvider {...methods}>
          <div className="fixed top-0 left-0 w-full h-full sm:px-5 sm:py-10 z-[9999] flex items-center justify-center bg-systemguideBg">
            <div className="relative bg-white w-full h-full max-w-4xl sm:rounded-[40px] transition-opacity duration-500 opacity-100">
              <form
                className="flex flex-col h-full justify-between py-10 px-3 sm:px-6 gap-2"
                onSubmit={methods.handleSubmit(onComplete)}
              >
                <Stack className="px-2 max-sm:pt-10">
                  {!selectedCategory && (
                    <Typography
                      textAlign="left"
                      fontVariant={selectedCategory ? 'h8' : 'h7'}
                    >
                      {selectedCategory
                        ? selectedCategoryName ?? ''
                        : `${t('guide.chooseCategory')}*`}
                    </Typography>
                  )}
                  {selectedCategory && (
                    <ProgressLine
                      totalSteps={totalSteps + 1}
                      currentStep={currentStep + 1}
                    />
                  )}
                  {!selectedCategory && (
                    <CategorySearch
                      handleInputChange={handleInputChange}
                      setOpenSearch={setOpenSearch}
                      t={t}
                    />
                  )}
                </Stack>
                <div className="overflow-y-auto flex flex-col justify-start flex-1">
                  {!selectedCategory ? (
                    <SelectCategoryContent
                      categories={filteredCategories}
                      onSelectCategory={onSelectCategory}
                      expandedSearchResult={expandedSearchResult}
                    />
                  ) : (
                    <SystemGuideSteps
                      ref={scrollRef}
                      questions={questions}
                      currentStep={currentStep}
                      selectedCategory={selectedCategory}
                      locale={locale}
                      t={t}
                    />
                  )}
                  <Stack className="gap-5 px-2 pt-10">
                    <Typography textAlign="left" fontVariant="xs">
                      {t('guide.asteriskMeansMandatoryQuestion')}
                    </Typography>
                    <div className="flex flex-col-reverse md:flex-row justify-between gap-3">
                      <div className="flex flex-col sm:flex-row max-md:justify-between gap-1">
                        {selectedCategory && (
                          <button
                            type="button"
                            className={`bg-transparent lg:min-w-48 text-customGray-700 text-clamp-sm font-medium border border-customGray-300 uppercase rounded-[40px] px-6 py-2 ${
                              currentStep === 0
                                ? 'opacity-50 cursor-auto'
                                : 'global-hover'
                            }`}
                            onClick={handlePreviousStep}
                            disabled={completeGuideLoading}
                          >
                            {t('guide.btnPreviousQuestion')}
                          </button>
                        )}
                        <button
                          type="button"
                          className={`bg-transparent text-customGray-700 text-clamp-sm font-light underline px-6 py-2 global-hover ${
                            !selectedCategory && 'pl-0'
                          }`}
                          onClick={handleClose}
                          disabled={completeGuideLoading}
                        >
                          {t('guide.abortGuide')}
                        </button>
                      </div>
                      <div className="flex flex-col sm:flex-row max-md:justify-between gap-3">
                        {selectedCategory && (
                          <button
                            type="button"
                            className={cx(
                              'bg-primary-700 lg:min-w-48 text-white text-clamp-sm font-medium border border-primary-700 uppercase rounded-[40px] px-6 py-2',
                              {
                                'opacity-50 cursor-auto':
                                  currentStep === totalSteps || disabledNext,
                                hidden: currentStep === totalSteps,
                                'global-hover':
                                  currentStep !== totalSteps && !disabledNext,
                              }
                            )}
                            onClick={handleNextStep}
                            disabled={completeGuideLoading}
                          >
                            {t('guide.btnNextQuestion')}
                          </button>
                        )}
                        {currentStep === totalSteps && (
                          <button
                            className={cx(
                              'flex items-center justify-center gap-2 bg-primary-700 lg:min-w-48 text-white text-clamp-sm font-medium border border-primary-700 uppercase rounded-[40px] px-6 py-2',
                              {
                                'opacity-50 !cursor-default':
                                  disabledNext || completeGuideLoading,
                              }
                            )}
                            type="submit"
                            disabled={disabledNext || completeGuideLoading}
                          >
                            {completeGuideLoading && (
                              <Loader innerClassName="!h-4 !w-4" />
                            )}

                            {t('guide.showResult')}
                          </button>
                        )}
                      </div>
                    </div>
                  </Stack>
                </div>
              </form>
              <div className="absolute flex justify-center w-full h-12 bg-transparent top-0 sm:-top-6">
                <div className="flex flex-row w-full sm:w-fit h-full px-3 gap-1 bg-white shadow-md sm:rounded-[40px] justify-center items-center">
                  <IconExplore height={24} width={24} />
                  <Typography
                    fontVariant="sm"
                    className="w-fit pr-2 leading-none !text-primary-800 font-normal"
                  >
                    {t('common.systemGuide')}
                    {selectedCategoryName && ` - ${selectedCategoryName}`}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </FormProvider>
      )}

      {showConfirmation && (
        <ConfirmationModal
          isOpen={showConfirmation}
          onConfirm={methods.handleSubmit(confirmClose)}
          onCancel={handleClose}
          message={t('guide.confirmCloseGuide')}
          t={t}
        />
      )}
    </>
  );
};

export default SystemGuideModal;
